import moment from 'moment';
import { useCallback, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { useTodayMoment } from '@app/shared/hooks';
import { capitalizeFirstLetter } from '@app/shared/utils';

import { DEFAULT_END_TIME, DEFAULT_START_TIME } from '../data';

export const useCalendar = () => {
  const [calendarDataRefreshKey, setCalendarDataRefreshKey] = useState(0);
  const [displayedEmployeeIds, setDisplayedEmployeeIds] = useState<string[]>(
    []
  );
  const [activeWorkdayEmployeeIds, setActiveWorkdayEmployeeIds] = useState<
    string[]
  >([]);
  const [searchParams, setSearchParams] = useSearchParams();
  const [branchStartTime, setBranchStartTime] = useState(DEFAULT_START_TIME);
  const [branchEndTime, setBranchEndTime] = useState(DEFAULT_END_TIME);
  // Follows the clock: a tab opened yesterday must not keep yesterday as today.
  const todayMoment = useTodayMoment();

  const dateSearchParam = useMemo(
    () => searchParams.get('date'),
    [searchParams]
  );

  const selectedDateMoment = useMemo(() => {
    if (!dateSearchParam) {
      return todayMoment.clone();
    }

    const parsedDateMoment = moment(dateSearchParam, 'YYYY-MM-DD', true);

    if (!parsedDateMoment.isValid()) {
      return todayMoment.clone();
    }

    return parsedDateMoment.startOf('day');
  }, [dateSearchParam, todayMoment]);

  const isTodaySelected = useMemo(
    () => selectedDateMoment.isSame(todayMoment, 'day'),
    [selectedDateMoment, todayMoment]
  );

  const selectedDateTitle = useMemo(() => {
    const isCurrentYear = selectedDateMoment.year() === todayMoment.year();
    const dateFormat = isCurrentYear ? 'dddd D MMMM' : 'dddd D MMMM YYYY';

    const formattedDate = selectedDateMoment.locale('ru').format(dateFormat);

    return capitalizeFirstLetter(formattedDate);
  }, [selectedDateMoment, todayMoment]);

  const updateDateSearchParamHandler = useCallback(
    (nextDateMoment: moment.Moment) => {
      setSearchParams(
        (prevSearchParams) => {
          const nextSearchParams = new URLSearchParams(prevSearchParams);

          if (nextDateMoment.isSame(todayMoment, 'day')) {
            nextSearchParams.delete('date');
          } else {
            nextSearchParams.set('date', nextDateMoment.format('YYYY-MM-DD'));
          }

          return nextSearchParams;
        },
        { replace: true }
      );
    },
    [setSearchParams, todayMoment]
  );

  const refreshCalendarData = useCallback(() => {
    setCalendarDataRefreshKey((prev) => prev + 1);
  }, []);

  const updateDisplayedEmployeeIds = useCallback((employeeIds: string[]) => {
    setDisplayedEmployeeIds(employeeIds);
  }, []);

  const addDisplayedEmployeeId = useCallback((employeeId: string) => {
    setDisplayedEmployeeIds((prevEmployeeIds) => {
      if (prevEmployeeIds.includes(employeeId)) {
        return prevEmployeeIds;
      }

      return [...prevEmployeeIds, employeeId];
    });
  }, []);

  const removeDisplayedEmployeeId = useCallback((employeeId: string) => {
    setDisplayedEmployeeIds((prevEmployeeIds) =>
      prevEmployeeIds.filter(
        (currentEmployeeId) => currentEmployeeId !== employeeId
      )
    );
  }, []);

  const updateActiveWorkdayEmployeeIds = useCallback(
    (employeeIds: string[]) => {
      setActiveWorkdayEmployeeIds(employeeIds);
    },
    []
  );

  const addActiveWorkdayEmployeeId = useCallback((employeeId: string) => {
    setActiveWorkdayEmployeeIds((prevEmployeeIds) => {
      if (prevEmployeeIds.includes(employeeId)) {
        return prevEmployeeIds;
      }

      return [...prevEmployeeIds, employeeId];
    });
  }, []);

  const removeActiveWorkdayEmployeeId = useCallback((employeeId: string) => {
    setActiveWorkdayEmployeeIds((prevEmployeeIds) =>
      prevEmployeeIds.filter(
        (currentEmployeeId) => currentEmployeeId !== employeeId
      )
    );
  }, []);

  const goToPrevDayHandler = useCallback(() => {
    updateDateSearchParamHandler(selectedDateMoment.clone().subtract(1, 'day'));
  }, [selectedDateMoment, updateDateSearchParamHandler]);

  const goToNextDayHandler = useCallback(() => {
    updateDateSearchParamHandler(selectedDateMoment.clone().add(1, 'day'));
  }, [selectedDateMoment, updateDateSearchParamHandler]);

  const returnToTodayHandler = useCallback(() => {
    updateDateSearchParamHandler(todayMoment.clone());
  }, [todayMoment, updateDateSearchParamHandler]);

  const validateDateSearchParamHandler = useCallback(() => {
    if (!dateSearchParam) {
      return;
    }

    const parsedDateMoment = moment(dateSearchParam, 'YYYY-MM-DD', true);

    if (parsedDateMoment.isValid()) {
      return;
    }

    setSearchParams(
      (prevSearchParams) => {
        const nextSearchParams = new URLSearchParams(prevSearchParams);
        nextSearchParams.delete('date');

        return nextSearchParams;
      },
      { replace: true }
    );
  }, [dateSearchParam, setSearchParams]);

  return {
    todayMoment,
    selectedDateMoment,
    selectedDateTitle,
    isTodaySelected,
    goToPrevDayHandler,
    goToNextDayHandler,
    returnToTodayHandler,
    validateDateSearchParamHandler,
    calendarDataRefreshKey,
    refreshCalendarData,
    displayedEmployeeIds,
    updateDisplayedEmployeeIds,
    addDisplayedEmployeeId,
    removeDisplayedEmployeeId,
    activeWorkdayEmployeeIds,
    updateActiveWorkdayEmployeeIds,
    addActiveWorkdayEmployeeId,
    removeActiveWorkdayEmployeeId,
    branchStartTime,
    branchEndTime,
    setBranchStartTime,
    setBranchEndTime,
  };
};
